import { useCallback, useEffect, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';

import { ANTCard } from './components/ANTCard/ANTCard';
import { ApprovedApplication } from './components/ApprovedApplication/ApprovedApplication';
import { CancelledView } from './components/CancelledView/CancelledView';
import { CrossSellCard } from './components/CrossSellCard/CrossSellCard';
import { DeclinedApplication } from './components/DeclinedApplication/DeclinedApplication';
import { EndedPaymentPlanView } from './components/EndedPaymentPlanView/EndedPaymentPlanView';
import { ExpiredApplication } from './components/ExpiredApplication/ExpiredApplication';
import { NoPlansView } from './components/NoPlansView/NoPlansView';
import { NothingSelectedView } from './components/NothingSelectedView/NothingSelectedView';
import { OngoingLoanView } from './components/OngoingLoanView/OngoingLoanView';
import { OverdueBanner } from './components/OverdueBanner';
import { OverdueView } from './components/OverdueView/OverdueView';
import { PartialView } from './components/PartialView/PartialView';
import { PaymentPlanCard } from './components/PaymentPlanCard/PaymentPlanCard';
import { PaymentPlans } from './components/PaymentPlans/PaymentPlans';
import { PendingApplication } from './components/PendingApplication/PendingApplication';
import { PendingView } from './components/PendingView/PendingView';
import { PlanButton } from './components/PlanButton/PlanButton';
import { ScheduledView } from './components/ScheduledView/ScheduledView';
import { SelectedView } from './components/SelectedView/SelectedView';
import { SellerDirectoryBanner } from './components/SellerDirectoryBanner/SellerDirectoryBanner';
import { SellerDirectoryBannerTest } from './components/SellerDirectoryBanner/SellerDirectoryBannerTest';
import { SLSecurityCheckBanner } from './components/SLSecurityCheckBanner/SLSecurityCheckBanner';
import SpendingLimitCard from './components/SpendingLimitCard/SpendingLimitCard';
import { SpendingLimitDrawer } from './components/SpendingLimitDrawer/SpendingLimitDrawer';
import { Plan } from './Dashboard.types';
import { useAccount } from './hooks/useAccount';
import { useRefreshSpendingLimits } from './hooks/useRefreshSpendingLimits';
import {
  useGetSharedFunctionality,
  useGetBuyerSellerDirectory,
  useGetApplication,
} from '../../api/lending/lapiHooks';
import { LoadingScreen } from '../../components/LoadingScreen/LoadingScreen';
import { useMediaQuery } from '../../hooks/useMediaQuery';

function capitalize(name: string): string {
  if (!name) return '';
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}

export function Account() {
  const {
    ready,
    error,
    buyerFirstName,
    application,
    selectedPlan,
    setSelectedPlan,
    ongoingPaymentPlans,
    capturedPlans,
    cancelledPayments,
    iwocaPayApplication,
    rawApplication,
    endedPayments,
    isSoleTrader,
  } = useAccount();

  const { fetchApplication } = useGetApplication();
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const { sellers } = useGetBuyerSellerDirectory();
  const [isSpendingLimitDrawerOpen, setIsSpendingLimitDrawerOpen] =
    useState(false);
  const { functionality, loadingFunctionality } = useGetSharedFunctionality();

  const selectPaymentPlan = useCallback(
    (plan: Plan) => setSelectedPlan(plan),
    [setSelectedPlan],
  );

  const {
    refreshSpendingLimit,
    isLoading: loadingRefreshSpendingLimit,
    isError: refreshSpendingLimitError,
  } = useRefreshSpendingLimits(() => {
    fetchApplication();
  });

  const handleRefreshSpendingLimit = async () => {
    try {
      await refreshSpendingLimit();
    } catch {
      // do nothing
    }
  };

  useEffect(() => {
    if (!selectedPlan?.status) return;

    tracking.ampli.buyerDashboardViewed({
      buyer_dashboard_state: selectedPlan.status,
    });
  }, [selectedPlan?.status]);

  if (!ready && !error) {
    return <LoadingScreen />;
  }

  const renderIwocaPayApplicationFallback = () => {
    if (!iwocaPayApplication) return null;

    switch (iwocaPayApplication.status) {
      case 'declined':
        return <DeclinedApplication />;
      default:
        return (
          <NoPlansView
            setIsSpendingLimitDrawerOpen={setIsSpendingLimitDrawerOpen}
            loadingRefreshSpendingLimit={loadingRefreshSpendingLimit}
            refreshSpendingLimit={handleRefreshSpendingLimit}
          />
        );
    }
  };

  const renderSelectedComponent = () => {
    if (!selectedPlan) return <NothingSelectedView />;

    switch (selectedPlan.status) {
      case 'overdue':
        return <OverdueView paymentPlan={selectedPlan} />;
      case 'ongoing':
        return <OngoingLoanView paymentPlan={selectedPlan} />;
      case 'pending':
        return <PendingApplication />;
      case 'partial':
        return <PartialView application={selectedPlan} />;
      case 'approved':
        return <ApprovedApplication application={selectedPlan} />;
      case 'expired':
        return <ExpiredApplication />;
      case 'declined':
        return <DeclinedApplication application={selectedPlan} />;
      case 'ended':
        return <EndedPaymentPlanView />;
      case 'scheduled':
        return <ScheduledView plan={selectedPlan} />;
      case 'cancelled':
        return <CancelledView plan={selectedPlan} />;
      default:
        return null;
    }
  };

  const showSellerDirectoryBannerTest =
    rawApplication?.status !== 'declined' && isSoleTrader;

  const showSellerDirectoryLink =
    sellers && rawApplication?.status !== 'declined' && isSoleTrader === false; // isSoleTrader === null must not pass

  const isOverdue = ongoingPaymentPlans.some(
    (paymentPlan) => paymentPlan.status === 'overdue',
  );

  const hasPaymentPlans =
    application?.payLink ||
    capturedPlans.length > 0 ||
    ongoingPaymentPlans.length > 0 ||
    cancelledPayments.length > 0 ||
    endedPayments.length > 0;

  return (
    <div className="min-h-[calc(100vh-72px)] bg-structure-100 pt-[112px] text-left font-reg">
      <div
        className="px-5 pb-5 mx-auto grid max-w-[1136px] grid-cols-[350px_1fr] gap-x-3xl m:grid-cols-1"
        style={{
          gridTemplateAreas: isMobile
            ? "'welcome' 'application' 'banners' 'plans'"
            : "'welcome welcome' 'banners banners' 'application plan' 'plans plan'",
          gridTemplateRows: 'max-content max-content max-content 1fr',
        }}
      >
        {ready ? (
          <>
            <section
              className="mb-xl text-left"
              style={{ gridArea: 'welcome' }}
            >
              <div>
                {isOverdue && <OverdueBanner />}
                <ANTCard />
                <SLSecurityCheckBanner />
                {!loadingFunctionality &&
                  functionality?.is_cross_sell_eligible && <CrossSellCard />}
                <SpendingLimitDrawer
                  isOpen={isSpendingLimitDrawerOpen}
                  setIsOpen={setIsSpendingLimitDrawerOpen}
                  refreshSpendingLimit={handleRefreshSpendingLimit}
                  loadingRefreshSpendingLimit={loadingRefreshSpendingLimit}
                  isError={refreshSpendingLimitError}
                  application={iwocaPayApplication}
                />
              </div>
              <h1 className="m-0 mb-xl text-[35px] font-med">
                Welcome {capitalize(buyerFirstName)}!
              </h1>
              <SpendingLimitCard
                setIsSpendingLimitDrawerOpen={setIsSpendingLimitDrawerOpen}
                refreshSpendingLimit={handleRefreshSpendingLimit}
                loadingRefreshSpendingLimit={loadingRefreshSpendingLimit}
                application={iwocaPayApplication}
              />
            </section>
            <section
              className="mb-xl flex flex-row gap-xl sm:flex-col"
              style={{ gridArea: 'banners' }}
            >
              {showSellerDirectoryBannerTest && <SellerDirectoryBannerTest />}
              {showSellerDirectoryLink && <SellerDirectoryBanner />}
            </section>
            <section
              className="mb-xl text-left"
              style={{ gridArea: 'application' }}
            >
              {application && (
                <div>
                  <h3 className="mt-0">You have an outstanding application</h3>
                  <PlanButton
                    status={application?.status}
                    selectedPlanId={selectedPlan?.pay_link_id!}
                    selectPaymentPlan={selectPaymentPlan}
                    paymentPlan={application}
                    renderContent={renderSelectedComponent}
                  >
                    <PaymentPlanCard
                      created={application.payLink.created}
                      reference={application.payLink.reference}
                      sellerName={application.payLink.seller_trading_name}
                      amount={application.payLink.amount}
                      status={application.status}
                    />
                  </PlanButton>
                </div>
              )}
            </section>
            {hasPaymentPlans ? (
              <>
                <section style={{ gridArea: 'plans' }}>
                  <PaymentPlans
                    endedPayments={endedPayments}
                    ongoingPaymentPlans={ongoingPaymentPlans}
                    capturedPlans={capturedPlans}
                    cancelledPayments={cancelledPayments}
                    selectedPlan={selectedPlan}
                    selectPaymentPlan={selectPaymentPlan}
                    renderContent={renderSelectedComponent}
                  />
                </section>
                {!isMobile && (
                  <section style={{ gridArea: 'plan' }}>
                    <SelectedView plan={selectedPlan}>
                      {renderSelectedComponent()}
                    </SelectedView>
                  </section>
                )}
              </>
            ) : (
              renderIwocaPayApplicationFallback()
            )}
          </>
        ) : (
          <PendingView />
        )}
      </div>
    </div>
  );
}
